import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { useUploadTaskFieldValueFile } from '../hooks/useTaskFieldValueFileMutation';
import { parseFieldValueFile, buildFileUrl } from '../utils/fieldValueFile';
import { SignatureCanvas } from './SignatureCanvas';

interface SignatureFieldValueEditorProps {
  value: string | null;
  fieldId: string;
  taskId: string;
  disabled?: boolean;
}

/** Views the current signature and re-opens the capture canvas to replace it. */
export function SignatureFieldValueEditor({
  value,
  fieldId,
  taskId,
  disabled,
}: SignatureFieldValueEditorProps) {
  const { t } = useTranslation('customFields');
  const [open, setOpen] = useState(false);
  const { mutate: upload, isPending } = useUploadTaskFieldValueFile(taskId);

  const signature = parseFieldValueFile(value);

  function handleSave(blob: Blob) {
    const file = new File([blob], 'signature.png', { type: 'image/png' });
    upload({ fieldId, file }, { onSuccess: () => setOpen(false) });
  }

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && !isPending && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 text-sm hover:underline"
          onClick={(e) => e.stopPropagation()}
          data-testid="custom-fields-value-signature-trigger"
        >
          {signature ? (
            <img
              src={buildFileUrl(signature.storageKey)}
              alt={t('fieldValue.signature.signedBy')}
              className="h-6 max-w-24 object-contain"
            />
          ) : (
            <span className="text-muted-foreground">{t('fieldValue.signature.empty')}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[420px] p-3" onClick={(e) => e.stopPropagation()}>
        <SignatureCanvas onSave={handleSave} onCancel={() => setOpen(false)} disabled={isPending} />
      </PopoverContent>
    </Popover>
  );
}
